import { Component, ChangeDetectionStrategy, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { DrawerModule } from 'primeng/drawer';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { BankAccountDTO, BankAccountService } from '../../services/bank-account.service';
import {
  BankInboxItemDTO,
  BankMatchTargetType,
  BankMovementDTO,
  BankReconciliationActDTO,
  BankReconciliationService
} from '../../services/bank-reconciliation.service';
import { ChargeReadComponent } from '../charges/charge-read.component';
import { ChargeService } from '../../services/charge.service';
import { PaymentFormService } from '../../services/payment-form.service';
import { ReconciliationService, type ReconciliationDTO } from '../../services/reconciliation.service';
import {
  ChargeOperationalStatusEnum,
  getPaymentFormLabel,
  getPaymentProviderLabel,
  getPaymentRecordStatusLabel,
  type ChargeDTO,
  type PaymentFormDTO,
  type PaymentRecordDTO
} from '../../core/models';
import { httpErrorApiMessage } from '../../core/api-response-utils';
import { formValueToApiInstant } from '../../core/date-utils';

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

type PeriodPreset = 'today' | 'week' | 'month' | 'bimester' | 'semester' | 'year';

function periodRange(preset: PeriodPreset): Date[] {
  const to = startOfLocalDay(new Date());
  const from = new Date(to);
  switch (preset) {
    case 'today':
      break;
    case 'week':
      from.setDate(from.getDate() - 7);
      break;
    case 'month':
      from.setMonth(from.getMonth() - 1);
      break;
    case 'bimester':
      from.setMonth(from.getMonth() - 2);
      break;
    case 'semester':
      from.setMonth(from.getMonth() - 6);
      break;
    case 'year':
      from.setFullYear(from.getFullYear() - 1);
      break;
  }
  return [from, to];
}

function toLocalYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const AMOUNT_TOLERANCE = 0.005;

type MatchDrawerRow =
  | { kind: 'separator'; label: 'Recomendados' | 'Emparejados' | 'Ignorados' }
  | { kind: 'movement'; movement: BankMovementDTO };

function parseYmd(value?: string | null): string | null {
  if (!value) return null;
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

function formatYmd(ymd: string): string {
  const [year, month, day] = ymd.split('-');
  return `${day}/${month}/${year}`;
}

function formatMxn(amount: number): string {
  return amount.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

@Component({
  selector: 'app-bank-reconciliations-list',
  templateUrl: './bank-reconciliations-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host { display: flex; flex-direction: column; flex: 1 1 0%; min-height: 0; width: 100%; }
      :host ::ng-deep .bank-inbox-table.p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .bank-inbox-table .p-datatable-table-container,
      :host ::ng-deep .bank-inbox-table.p-datatable-flex-scrollable > .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .bank-inbox-table .p-datatable-thead > tr > th,
      :host ::ng-deep .bank-candidates-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      :host ::ng-deep .bank-match-drawer.p-drawer,
      :host ::ng-deep .p-drawer.bank-match-drawer {
        display: flex;
        flex-direction: column;
      }
      :host ::ng-deep .bank-match-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 0.5rem 0.75rem;
        flex-shrink: 0;
      }
      :host ::ng-deep .bank-match-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 0.625rem 0.75rem;
        display: flex;
        flex-direction: column;
        flex: 1 1 0;
        min-height: 0;
        overflow: hidden;
        font-size: 0.75rem;
      }
      :host ::ng-deep .bank-candidates-table .p-datatable-thead > tr > th,
      :host ::ng-deep .bank-candidates-table .p-datatable-tbody > tr > td {
        padding: 0.3rem 0.45rem;
        font-size: 0.75rem;
      }
      :host ::ng-deep .bank-candidates-table .p-datatable-tbody > tr:hover > td {
        background: rgb(224 242 254);
      }
      :host ::ng-deep .bank-candidates-table .p-button {
        font-size: 0.7rem;
        padding: 0.2rem 0.45rem;
      }
      :host ::ng-deep .bank-candidates-table.p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      ::ng-deep .account-tooltip .p-tooltip-text {
        white-space: pre-line;
      }
      :host ::ng-deep .bank-candidates-table .p-datatable-table-container,
      :host ::ng-deep .bank-candidates-table.p-datatable-flex-scrollable > .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
    `
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    FloatLabelModule,
    SelectModule,
    DatePickerModule,
    DrawerModule,
    TagModule,
    TooltipModule,
    ConfirmDialogModule,
    DialogModule,
    CheckboxModule,
    InputTextModule,
    TextareaModule,
    ChargeReadComponent,
    PERMISSION_UI_DIRECTIVES
  ],
  providers: [ConfirmationService]
})
export class BankReconciliationsListComponent {
  private service = inject(BankReconciliationService);
  private accountService = inject(BankAccountService);
  private chargeService = inject(ChargeService);
  private paymentFormService = inject(PaymentFormService);
  private reconciliationService = inject(ReconciliationService);
  private fb = inject(FormBuilder);
  private toastr = inject(ToastrService);
  private confirmation = inject(ConfirmationService);

  accounts = signal<BankAccountDTO[]>([]);
  drawerAccountOptions = computed(() => [
    { id: 0, alias: 'Todas' },
    ...this.accounts()
  ]);
  inbox = signal<BankInboxItemDTO[]>([]);
  showMatched = signal(true);
  activePreset = signal<PeriodPreset | null>('month');
  selectedItem = signal<BankInboxItemDTO | null>(null);
  loading = signal(false);
  importing = signal(false);
  matching = signal(false);
  busyKey = signal<string | null>(null);
  importDialog = signal(false);
  importAccountId = signal<number | null>(null);
  importFileName = signal('');
  replaceUnmatched = signal(false);
  importResultDialog = signal(false);
  importResult = signal<{ account: string; file: string; inserted: number; skipped: number; matched: number; error?: string } | null>(null);
  matchDialog = signal(false);
  matchAccountId = signal<number | null>(null);
  private importFile: File | null = null;
  matchDrawer = signal(false);
  matchDetailLoading = signal(false);
  matchCharge = signal<ChargeDTO | null>(null);
  selectedCharge = signal<ChargeDTO | null>(null);
  chargeReadOpen = signal(false);
  paymentFormOptions = signal<PaymentFormDTO[]>([]);
  payChargeDialogVisible = signal(false);
  payChargeLoading = signal(false);
  payChargePaymentFormText = signal<string | null>(null);
  private pendingMovement = signal<BankMovementDTO | null>(null);
  payChargeForm = this.fb.group({
    paymentFormId: [null as number | null, Validators.required],
    reference: ['', [Validators.required, Validators.maxLength(50)]],
    bank: ['', [Validators.maxLength(255)]],
    description: ['', [Validators.maxLength(1000)]],
    paymentDate: [null as Date | null, Validators.required],
    sendReceiptEmail: [true]
  });
  matchSettlement = signal<ReconciliationDTO | null>(null);
  pickableMovements = signal<BankMovementDTO[]>([]);
  drawerAccountId = signal<number | null>(null);
  loadingMovements = signal(false);
  importedTotal = signal(0);
  importedDebits = signal(0);
  displayMovements = computed(() => {
    const item = this.selectedItem();
    if (!item) return [];
    const byDate = (left: BankMovementDTO, right: BankMovementDTO) => this.compareBookedAt(left.bookedAt, right.bookedAt);
    const inAccount = (movement: BankMovementDTO) => this.matchesDrawerAccount(movement);
    const candidates = [...(item.candidates ?? [])]
      .filter(movement => !this.isIgnored(movement) && !this.isMatched(movement) && inAccount(movement))
      .sort(byDate);
    const candidateIds = new Set(candidates.map(movement => movement.id));
    const available = this.pickableMovements().filter(movement => !this.isIgnored(movement) && inAccount(movement));
    const rest = available.filter(movement => !this.isMatched(movement) && !candidateIds.has(movement.id)).sort(byDate);
    const matchedIds = new Set<number>();
    const matched = [
      ...available.filter(movement => this.isMatched(movement)),
      ...(item.candidates ?? []).filter(movement => this.isMatched(movement) && inAccount(movement))
    ].filter(movement => {
      if (movement.id == null || matchedIds.has(movement.id)) return false;
      matchedIds.add(movement.id);
      return true;
    }).sort(byDate);
    const ignored = this.pickableMovements().filter(movement => this.isIgnored(movement) && inAccount(movement)).sort(byDate);
    const rows: MatchDrawerRow[] = [];
    if (candidates.length) {
      rows.push({ kind: 'separator', label: 'Recomendados' });
      candidates.forEach(movement => rows.push({ kind: 'movement', movement }));
    }
    rest.forEach(movement => rows.push({ kind: 'movement', movement }));
    if (matched.length) {
      rows.push({ kind: 'separator', label: 'Emparejados' });
      matched.forEach(movement => rows.push({ kind: 'movement', movement }));
    }
    if (ignored.length) {
      rows.push({ kind: 'separator', label: 'Ignorados' });
      ignored.forEach(movement => rows.push({ kind: 'movement', movement }));
    }
    return rows;
  });
  emptyMatchHint = computed(() => {
    if (this.displayMovements().length) return '';
    if (this.drawerAccountId() != null) {
      return 'Esta cuenta no tiene abonos para mostrar.';
    }
    if (this.importedTotal() === 0) {
      return 'Aún no hay movimientos importados. Importa un CSV y vuelve a abrir el recibo.';
    }
    if (this.importedDebits() > 0 && this.importedDebits() === this.importedTotal()) {
      return 'El CSV se importó, pero todas las filas quedaron como cargos (débitos). Solo se emparejan abonos: columna Abono o montos positivos.';
    }
    return 'No hay abonos para este recibo. Revisa que el CSV tenga columna Abono.';
  });

  filterForm = this.fb.group({
    paidAtRange: [periodRange('month') as Date[] | null]
  });
  private applyingPreset = false;

  constructor() {
    this.accountService.list({}, { page: 0, size: 100 }).subscribe({
      next: res => this.accounts.set(res.body ?? []),
      error: () => this.toastr.error('No se pudieron cargar las cuentas')
    });
    this.filterForm.controls.paidAtRange.valueChanges.subscribe(range => {
      if (!this.applyingPreset) this.activePreset.set(null);
      if (range && Array.isArray(range) && range[0] && !range[1]) return;
      this.refresh();
    });
    this.refresh();
    this.paymentFormService.listPaymentForms({ state: 'ACTIVE' }, { page: 0, size: 500 }).subscribe({
      next: res => this.paymentFormOptions.set(res.body ?? [])
    });
  }

  refresh(): void {
    this.loading.set(true);
    const period = this.periodParams();
    this.service.companyInbox({ ...period, includeMatched: this.showMatched() }, { page: 0, size: 100 }).subscribe({
      next: res => {
        this.inbox.set(res.body ?? []);
        const current = this.selectedItem();
        if (current) {
          const next = (res.body ?? []).find(item => item.kind === current.kind && item.id === current.id) ?? null;
          this.selectedItem.set(next);
        }
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.toastr.error('No se pudo cargar el inbox');
      }
    });
  }

  onShowMatchedChange(checked: boolean): void {
    this.showMatched.set(checked);
    this.refresh();
  }

  openImportDialog(): void {
    this.importAccountId.set(this.accounts()[0]?.id ?? null);
    this.importFile = null;
    this.importFileName.set('');
    this.replaceUnmatched.set(false);
    this.importDialog.set(true);
  }

  onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.importFile = file;
    this.importFileName.set(file?.name ?? '');
    input.value = '';
  }

  submitImport(): void {
    const accountId = this.importAccountId();
    const file = this.importFile;
    if (accountId == null || !file || this.importing()) return;
    const account = this.accounts().find(item => item.id === accountId)?.alias ?? 'Cuenta';
    this.importing.set(true);
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      this.service.importStatement(accountId, {
        name: file.name,
        contentType: file.type || 'text/csv',
        contentBase64: base64,
        replaceUnmatched: this.replaceUnmatched()
      }).subscribe({
        next: res => {
          this.importing.set(false);
          this.importDialog.set(false);
          this.importResult.set({
            account,
            file: file.name,
            inserted: res.body?.insertedCount ?? 0,
            skipped: res.body?.skippedCount ?? 0,
            matched: res.body?.matchedByTrackCode ?? 0
          });
          this.importResultDialog.set(true);
          this.refresh();
          if (this.matchDrawer()) this.loadPickableMovements();
        },
        error: (err) => {
          this.importing.set(false);
          this.importDialog.set(false);
          this.importResult.set({
            account,
            file: file.name,
            inserted: 0,
            skipped: 0,
            matched: 0,
            error: httpErrorApiMessage(err, 'No se pudo importar el CSV')
          });
          this.importResultDialog.set(true);
        }
      });
    };
    reader.readAsDataURL(file);
  }

  openMatchDialog(): void {
    this.matchAccountId.set(this.accounts()[0]?.id ?? null);
    this.matchDialog.set(true);
  }

  runMatch(): void {
    const accountId = this.matchAccountId();
    if (accountId == null || this.matching()) return;
    this.matching.set(true);
    this.service.runMatch(accountId).subscribe({
      next: res => {
        this.matching.set(false);
        this.matchDialog.set(false);
        const acts = res.body ?? [];
        acts.forEach(act => this.applyAct(act));
        this.toastr.success(`${acts.length} actos creados`);
      },
      error: () => {
        this.matching.set(false);
        this.toastr.error('No se pudo ejecutar el matching');
      }
    });
  }

  openMatchDrawer(item: BankInboxItemDTO): void {
    this.selectedItem.set(item);
    this.loadMatchTarget(item);
    this.loadPickableMovements();
    this.matchDrawer.set(true);
  }

  onMatchDrawerVisibleChange(visible: boolean): void {
    this.matchDrawer.set(visible);
    if (!visible) {
      this.clearMatchSelection();
    }
  }

  confirm(item: BankInboxItemDTO): void {
    if (item.suggestionActId == null) return;
    this.service.confirm(item.suggestionActId).subscribe({
      next: res => {
        if (res.body) this.applyAct(res.body);
        this.closeMatchDrawer();
        this.toastr.success('Sugerencia confirmada');
      },
      error: () => this.toastr.error('No se pudo confirmar')
    });
  }

  matchSelected(movement: BankMovementDTO): void {
    const item = this.selectedItem();
    if (!item?.kind || item.id == null || movement.id == null || this.busyKey()) return;
    const warning = this.mismatchWarning(movement, item);
    const proceed = () => this.reconcileMovement(item, movement);
    if (!warning) {
      proceed();
      return;
    }
    this.confirmation.confirm({
      header: 'Conciliar con diferencias',
      message: `${warning} ¿Deseas conciliar de todos modos?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Conciliar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-warning',
      rejectButtonStyleClass: 'p-button-text',
      accept: proceed
    });
  }

  private reconcileMovement(item: BankInboxItemDTO, movement: BankMovementDTO): void {
    if (this.chargeNeedsPayment(item)) {
      this.openPayChargeDialog(movement);
      return;
    }
    this.submitMatch(item, movement);
  }

  openPayChargeDialog(movement: BankMovementDTO): void {
    const booked = parseYmd(movement.bookedAt);
    const paymentDate = booked ? new Date(`${booked}T00:00:00`) : new Date();
    this.pendingMovement.set(movement);
    this.payChargePaymentFormText.set(movement.paymentFormText?.trim() || null);
    this.payChargeForm.reset({
      paymentFormId: movement.paymentFormId ?? null,
      reference: (movement.trackCode || movement.reference || '').slice(0, 50),
      bank: movement.orderingBank || '',
      description: movement.description || '',
      paymentDate,
      sendReceiptEmail: true
    });
    this.payChargeDialogVisible.set(true);
  }

  closePayChargeDialog(): void {
    if (this.payChargeLoading()) return;
    this.payChargeDialogVisible.set(false);
    this.pendingMovement.set(null);
    this.payChargePaymentFormText.set(null);
  }

  submitPayCharge(): void {
    const item = this.selectedItem();
    const movement = this.pendingMovement();
    if (!item || item.id == null || !movement || this.payChargeLoading()) return;
    if (this.payChargeForm.invalid) {
      this.payChargeForm.markAllAsTouched();
      this.toastr.error('Completa todos los campos del pago.');
      return;
    }
    const raw = this.payChargeForm.getRawValue();
    const paymentDate = formValueToApiInstant(raw.paymentDate);
    if (!paymentDate) {
      this.toastr.error('La fecha de pago no es válida.');
      return;
    }
    const payload = {
      paymentFormId: Number(raw.paymentFormId),
      reference: String(raw.reference ?? '').trim(),
      bank: String(raw.bank ?? '').trim() || undefined,
      description: String(raw.description ?? '').trim() || undefined,
      paymentDate,
      sendReceiptEmail: raw.sendReceiptEmail === true
    };
    this.payChargeLoading.set(true);
    this.chargeService.applyPayment(item.id, payload)
      .pipe(finalize(() => this.payChargeLoading.set(false)))
      .subscribe({
        next: res => {
          if (!res.ok || !res.body) {
            this.toastr.error(res.message || 'No se pudo aplicar el pago');
            return;
          }
          this.payChargeDialogVisible.set(false);
          this.pendingMovement.set(null);
          this.payChargePaymentFormText.set(null);
          this.matchCharge.set(res.body);
          this.patchItem(item, { paid: true });
          this.submitMatch({ ...item, paid: true }, movement);
        },
        error: err => this.toastr.error(httpErrorApiMessage(err, 'No se pudo aplicar el pago'))
      });
  }

  private submitMatch(item: BankInboxItemDTO, movement: BankMovementDTO): void {
    if (!item.kind || item.id == null || movement.id == null || this.busyKey()) return;
    this.busyKey.set(this.movementKey(movement));
    this.service.createManual({
      targetType: item.kind as BankMatchTargetType,
      targetId: item.id,
      movementIds: [movement.id]
    }).subscribe({
      next: res => {
        this.busyKey.set(null);
        if (res.body) this.applyAct(res.body);
        else this.markMatched(item, movement);
        this.closeMatchDrawer();
        this.toastr.success('Conciliado');
      },
      error: (err) => {
        this.busyKey.set(null);
        this.toastr.error(httpErrorApiMessage(err, 'No se pudo conciliar'));
      }
    });
  }

  ignore(movement: BankMovementDTO): void {
    const accountId = movement.bankAccountId;
    if (accountId == null || movement.id == null || this.busyKey()) return;
    this.service.ignoreMovement(accountId, movement.id).subscribe({
      next: () => {
        this.markIgnored(movement);
        this.toastr.success('Movimiento ignorado');
      },
      error: () => this.toastr.error('No se pudo ignorar')
    });
  }

  reactivate(movement: BankMovementDTO): void {
    const accountId = movement.bankAccountId;
    if (accountId == null || movement.id == null || this.busyKey()) return;
    const key = this.reactivateKey(movement);
    this.busyKey.set(key);
    this.service.reactivateMovement(accountId, movement.id).subscribe({
      next: () => {
        this.busyKey.set(null);
        this.patchMovement(movement.id!, { movementStatus: 'PENDING' });
        this.toastr.success('Movimiento reactivado');
      },
      error: (err) => {
        this.busyKey.set(null);
        this.toastr.error(httpErrorApiMessage(err, 'No se pudo reactivar'));
      }
    });
  }

  deleteIgnored(movement: BankMovementDTO): void {
    if (movement.id == null || this.busyKey()) return;
    this.confirmation.confirm({
      header: 'Eliminar movimiento',
      message: 'El abono se borrará de la base de datos.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.submitDeleteIgnored(movement)
    });
  }

  private submitDeleteIgnored(movement: BankMovementDTO): void {
    const accountId = movement.bankAccountId;
    if (accountId == null || movement.id == null || this.busyKey()) return;
    const key = this.deleteKey(movement);
    this.busyKey.set(key);
    this.service.deleteMovement(accountId, movement.id).subscribe({
      next: () => {
        this.busyKey.set(null);
        this.removeMovement(movement.id!);
        this.toastr.success('Movimiento eliminado');
      },
      error: (err) => {
        this.busyKey.set(null);
        this.toastr.error(httpErrorApiMessage(err, 'No se pudo eliminar'));
      }
    });
  }

  reverseItem(item: BankInboxItemDTO): void {
    this.unmatchAct(this.reverseActId(item), this.rowKey(item));
  }

  reverseSelected(): void {
    const item = this.selectedItem();
    if (item) this.unmatchAct(this.reverseActId(item), this.drawerKey(item));
  }

  unmatchMovement(movement: BankMovementDTO): void {
    this.unmatchAct(movement.actId ?? this.reverseActId(this.selectedItem()), this.movementKey(movement));
  }

  rowKey(item: BankInboxItemDTO | null | undefined): string {
    return `row:${item?.kind ?? ''}:${item?.id ?? ''}`;
  }

  drawerKey(item: BankInboxItemDTO | null | undefined): string {
    return `drawer:${item?.kind ?? ''}:${item?.id ?? ''}`;
  }

  movementKey(movement: BankMovementDTO | null | undefined): string {
    return `movement:${movement?.id ?? ''}`;
  }

  reactivateKey(movement: BankMovementDTO | null | undefined): string {
    return `reactivate:${movement?.id ?? ''}`;
  }

  deleteKey(movement: BankMovementDTO | null | undefined): string {
    return `delete:${movement?.id ?? ''}`;
  }

  isBusy(key: string): boolean {
    return this.busyKey() === key;
  }

  private unmatchAct(actId: number | null | undefined, key: string): void {
    if (actId == null || this.busyKey()) return;
    this.confirmation.confirm({
      header: 'Desemparejar',
      message: 'Se quitará el emparejamiento de este registro.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Desemparejar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-warning',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.submitUnmatch(actId, key)
    });
  }

  private submitUnmatch(actId: number, key: string): void {
    if (this.busyKey()) return;
    this.busyKey.set(key);
    this.service.reverse(actId).subscribe({
      next: res => {
        this.busyKey.set(null);
        if (res.body) this.applyAct(res.body);
        this.toastr.success('Desemparejado');
      },
      error: (err) => {
        this.busyKey.set(null);
        this.toastr.error(httpErrorApiMessage(err, 'No se pudo desemparejar'));
      }
    });
  }

  kindLabel(kind?: BankMatchTargetType): string {
    return kind === 'SETTLEMENT' ? 'Liquidación' : 'Recibo';
  }

  openChargeRead(row: BankInboxItemDTO, event?: Event): void {
    event?.stopPropagation();
    if (row.kind !== 'CHARGE' || row.id == null) return;
    this.chargeService.listCharges({ id: row.id }, { page: 0, size: 1 }).subscribe({
      next: res => {
        const charge = res.body?.[0];
        if (!charge) {
          this.toastr.error('No se pudo cargar el recibo');
          return;
        }
        this.selectedCharge.set(charge);
        this.chargeReadOpen.set(true);
      },
      error: err => this.toastr.error(httpErrorApiMessage(err, 'No se pudo cargar el recibo'))
    });
  }

  onChargeReadVisibleChange(visible: boolean): void {
    this.chargeReadOpen.set(visible);
    if (!visible) this.selectedCharge.set(null);
  }

  applyPreset(preset: PeriodPreset): void {
    this.applyingPreset = true;
    this.activePreset.set(preset);
    this.filterForm.controls.paidAtRange.setValue(periodRange(preset));
    this.applyingPreset = false;
  }

  private periodParams(): { from?: string; to?: string } {
    const range = this.resolvePeriod(this.filterForm.controls.paidAtRange.value);
    if (!range) return {};
    return { from: toLocalYmd(range[0]), to: toLocalYmd(range[1]) };
  }

  private resolvePeriod(range: Date[] | null | undefined): [Date, Date] | null {
    if (!range || !Array.isArray(range) || !range[0]) return null;
    return [startOfLocalDay(range[0]), startOfLocalDay(range[1] ?? range[0])];
  }

  chargeCustomerName(charge: ChargeDTO): string {
    const customer = charge.sale?.customer;
    if (!customer) return '-';
    return customer.comercialName || customer.legalName || customer.taxId || '-';
  }

  chargeStatusLabel(status?: string): string {
    const labels: Record<string, string> = {
      SCHEDULED: 'Programado',
      PENDING: 'Pendiente',
      DUE: 'Vencido',
      PAID: 'Pagado',
      FAILED: 'Fallido',
      EXPIRED: 'Expirado',
      CANCELLED: 'Cancelado'
    };
    return status ? (labels[status] ?? status) : '-';
  }

  chargeNeedsPayment(item: BankInboxItemDTO | null | undefined = this.selectedItem()): boolean {
    if (item?.kind !== 'CHARGE') return false;
    if (item.paid === false) return true;
    const charge = this.matchCharge();
    return charge?.status != null && charge.status !== ChargeOperationalStatusEnum.PAID;
  }

  paymentFormLabel(payment: PaymentRecordDTO): string {
    return getPaymentFormLabel(payment.paymentForm) || '-';
  }

  movementPaymentFormLabel(movement: BankMovementDTO): string {
    const form = this.paymentFormOptions().find(option => option.id === movement.paymentFormId);
    const label = getPaymentFormLabel(form);
    if (label) return label;
    return movement.paymentFormText?.trim() || '-';
  }

  paymentStatusLabel(payment: PaymentRecordDTO): string {
    return getPaymentRecordStatusLabel(payment.status) || '-';
  }

  paymentProviderLabel(payment: PaymentRecordDTO): string {
    return getPaymentProviderLabel(payment.paymentProvider) || '-';
  }

  private compareBookedAt(left?: string | null, right?: string | null): number {
    if (!left && !right) return 0;
    if (!left) return 1;
    if (!right) return -1;
    return left.localeCompare(right);
  }

  separatorLabel(row: MatchDrawerRow): 'Recomendados' | 'Emparejados' | 'Ignorados' | null {
    return row.kind === 'separator' ? row.label : null;
  }

  accountAlias(movement: BankMovementDTO): string {
    return this.accountOf(movement)?.alias || '—';
  }

  accountTooltip(movement: BankMovementDTO): string {
    const account = this.accountOf(movement);
    if (!account) return '';
    return `Nombre: ${account.alias || '—'}\nBanco: ${account.bankName || '—'}\nCuenta: ${account.clabe || '—'}`;
  }

  private accountOf(movement: BankMovementDTO): BankAccountDTO | undefined {
    return this.accounts().find(account => account.id === movement.bankAccountId);
  }

  private matchesDrawerAccount(movement: BankMovementDTO): boolean {
    const accountId = this.drawerAccountId();
    return accountId == null || movement.bankAccountId === accountId;
  }

  isIgnored(movement: BankMovementDTO): boolean {
    return movement.movementStatus === 'IGNORED';
  }

  movementFrom(row: MatchDrawerRow): BankMovementDTO | null {
    return row.kind === 'movement' ? row.movement : null;
  }

  isCandidate(movement: BankMovementDTO): boolean {
    return !!this.selectedItem()?.candidates?.some(item => item.id === movement.id);
  }

  isMatched(movement: BankMovementDTO): boolean {
    return movement.movementStatus === 'MATCHED';
  }

  canMatchMovement(movement: BankMovementDTO): boolean {
    return !this.selectedItem()?.matched && !this.isMatched(movement)
      && (movement.movementStatus === 'PENDING' || movement.movementStatus === 'PARTIAL');
  }

  canUnmatch(item: BankInboxItemDTO | null | undefined): boolean {
    return this.reverseActId(item) != null;
  }

  canUnmatchMovement(movement: BankMovementDTO): boolean {
    return this.isMatched(movement) && (movement.actId != null || this.canUnmatch(this.selectedItem()));
  }

  private hasAmountMismatch(movement: BankMovementDTO, item = this.selectedItem()): boolean {
    if (!item || item.amount == null || movement.amount == null) return false;
    return Math.abs(Number(item.amount) - Number(movement.amount)) > AMOUNT_TOLERANCE;
  }

  private hasDateMismatch(movement: BankMovementDTO, item = this.selectedItem()): boolean {
    const itemDate = parseYmd(item?.date);
    const movementDate = parseYmd(movement.bookedAt);
    return !!itemDate && !!movementDate && itemDate !== movementDate;
  }

  mismatchTooltip(movement: BankMovementDTO, item = this.selectedItem()): string | null {
    const amount = this.hasAmountMismatch(movement, item);
    const date = this.hasDateMismatch(movement, item);
    if (amount && date) return 'No corresponde el monto ni la fecha';
    if (amount) return 'No corresponde el monto';
    if (date) return 'No corresponde la fecha';
    return null;
  }

  mismatchWarning(movement: BankMovementDTO, item = this.selectedItem()): string | null {
    if (!item) return null;
    const parts: string[] = [];
    if (this.hasAmountMismatch(movement, item) && item.amount != null && movement.amount != null) {
      parts.push(`el monto (${formatMxn(Number(item.amount))} vs ${formatMxn(Number(movement.amount))})`);
    }
    const itemDate = parseYmd(item.date);
    const movementDate = parseYmd(movement.bookedAt);
    if (this.hasDateMismatch(movement, item) && itemDate && movementDate) {
      parts.push(`la fecha (${formatYmd(itemDate)} vs ${formatYmd(movementDate)})`);
    }
    if (!parts.length) return null;
    return `No coinciden ${parts.join(' y ')}.`;
  }

  private closeMatchDrawer(): void {
    this.matchDrawer.set(false);
    this.clearMatchSelection();
  }

  private clearMatchSelection(): void {
    this.selectedItem.set(null);
    this.matchCharge.set(null);
    this.matchSettlement.set(null);
    this.pendingMovement.set(null);
    this.payChargePaymentFormText.set(null);
    this.payChargeDialogVisible.set(false);
  }

  statusLabel(item: BankInboxItemDTO): string {
    return item.matched ? 'Emparejado' : 'Pendiente';
  }

  statusSeverity(item: BankInboxItemDTO): 'success' | 'warn' {
    return item.matched ? 'success' : 'warn';
  }

  private reverseActId(item: BankInboxItemDTO | null | undefined): number | null {
    if (!item) return null;
    return item.confirmedActId ?? item.suggestionActId ?? null;
  }

  private loadMatchTarget(item: BankInboxItemDTO): void {
    this.matchCharge.set(null);
    this.matchSettlement.set(null);
    if (item.kind === 'CHARGE' && item.id != null) {
      this.matchDetailLoading.set(true);
      this.chargeService.listCharges({ id: item.id }, { page: 0, size: 1 }).subscribe({
        next: res => {
          const charge = res.body?.[0] ?? null;
          this.matchCharge.set(charge);
          if (charge) {
            this.patchItem(item, { paid: charge.status === ChargeOperationalStatusEnum.PAID });
          }
          this.matchDetailLoading.set(false);
        },
        error: () => {
          this.matchDetailLoading.set(false);
          this.toastr.error('No se pudo cargar el recibo');
        }
      });
      return;
    }
    if (item.kind === 'SETTLEMENT' && item.id != null) {
      this.matchDetailLoading.set(true);
      this.reconciliationService.getById(item.id).subscribe({
        next: res => {
          this.matchSettlement.set(res.body ?? null);
          this.matchDetailLoading.set(false);
        },
        error: () => {
          this.matchDetailLoading.set(false);
          this.toastr.error('No se pudo cargar la liquidación');
        }
      });
    }
  }

  private applyAct(act: BankReconciliationActDTO): void {
    const entries = act.entries ?? [];
    const movementIds = entries
      .filter(entry => entry.partyType === 'MOVEMENT' && entry.partyId != null)
      .map(entry => entry.partyId!);
    const reversed = act.status === 'REVERSED';
    for (const entry of entries) {
      if ((entry.partyType !== 'CHARGE' && entry.partyType !== 'SETTLEMENT') || entry.partyId == null) continue;
      const current = this.inbox().find(item => item.kind === entry.partyType && item.id === entry.partyId);
      if (!current) continue;
      if (reversed) {
        this.patchItem(current, { matched: false, confirmedActId: undefined, suggestionActId: undefined });
      } else if (act.confirmed) {
        this.patchItem(current, { matched: true, confirmedActId: act.id, suggestionActId: undefined, candidates: [] });
      } else {
        const known = this.pickableMovements().filter(movement => movementIds.includes(movement.id!));
        this.patchItem(current, {
          matched: false,
          suggestionActId: act.id,
          candidates: known.length ? known : current.candidates
        });
      }
    }
    if (reversed) {
      movementIds.forEach(id => this.patchMovement(id, { movementStatus: 'PENDING', actId: undefined }));
    } else if (act.confirmed) {
      movementIds.forEach(id => this.patchMovement(id, { movementStatus: 'MATCHED', actId: act.id }));
    }
  }

  private markMatched(item: BankInboxItemDTO, movement: BankMovementDTO): void {
    this.patchItem(item, { matched: true, candidates: [] });
    if (movement.id != null) this.patchMovement(movement.id, { movementStatus: 'MATCHED' });
  }

  private patchItem(item: BankInboxItemDTO, patch: Partial<BankInboxItemDTO>): void {
    this.inbox.update(items => {
      if (!this.showMatched() && patch.matched) {
        return items.filter(current => !(current.kind === item.kind && current.id === item.id));
      }
      return items.map(current =>
        current.kind === item.kind && current.id === item.id ? { ...current, ...patch } : current
      );
    });
    const selected = this.selectedItem();
    if (selected?.kind === item.kind && selected.id === item.id) {
      this.selectedItem.set({ ...selected, ...patch });
    }
  }

  private patchMovement(id: number, patch: Partial<BankMovementDTO>): void {
    this.pickableMovements.update(items => items.map(movement =>
      movement.id === id ? { ...movement, ...patch } : movement
    ));
    const selected = this.selectedItem();
    if (!selected?.candidates?.some(movement => movement.id === id)) return;
    this.patchItem(selected, {
      candidates: selected.candidates.map(movement => movement.id === id ? { ...movement, ...patch } : movement)
    });
  }

  private markIgnored(movement: BankMovementDTO): void {
    if (movement.id == null) return;
    const ignored = { ...movement, movementStatus: 'IGNORED' as const };
    this.pickableMovements.update(items => {
      const exists = items.some(item => item.id === movement.id);
      return exists
        ? items.map(item => item.id === movement.id ? ignored : item)
        : [...items, ignored];
    });
    const selected = this.selectedItem();
    if (!selected?.candidates?.some(item => item.id === movement.id)) return;
    this.patchItem(selected, {
      candidates: selected.candidates.map(item => item.id === movement.id ? ignored : item)
    });
  }

  private removeMovement(id: number): void {
    this.pickableMovements.update(items => items.filter(movement => movement.id !== id));
    const selected = this.selectedItem();
    if (!selected?.candidates?.some(movement => movement.id === id)) return;
    this.patchItem(selected, {
      candidates: selected.candidates.filter(movement => movement.id !== id)
    });
  }

  private loadPickableMovements(): void {
    this.loadingMovements.set(true);
    this.service.listCompanyMovements({}, { page: 0, size: 200, sort: ['bookedAt,asc'] }).subscribe({
      next: res => {
        const all = res.body ?? [];
        this.importedTotal.set(all.length);
        this.importedDebits.set(all.filter(movement => movement.lineType === 'DEBIT').length);
        this.pickableMovements.set(all.filter(movement =>
          movement.lineType === 'CREDIT'
          && (movement.movementStatus === 'PENDING'
            || movement.movementStatus === 'PARTIAL'
            || movement.movementStatus === 'MATCHED'
            || movement.movementStatus === 'IGNORED')
        ));
        this.loadingMovements.set(false);
      },
      error: (err) => {
        this.loadingMovements.set(false);
        this.toastr.error(httpErrorApiMessage(err, 'No se pudieron cargar los abonos'));
      }
    });
  }
}
